import { Box, Grid, SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { getDateStringForClosed, isCurrencyWithWholeNumbers } from "../common/RoundHelper";
import { Button } from "../reusable-components";
import { IsLeo, themeC } from "../theme/themehelper";
import { DbetArrowRight } from "../images/dbeticons";
import { ShallowRound } from "../models/round";

interface RoundBoxSettledContentProps {
    round: ShallowRound;
    theme: string;
    t: (key: string) => string;
}

// These themes show the badge in the tile header instead (RoundBoxWithButton). Parent must be position: relative.
export function LiveBadge({ theme, t }: { theme: string; t: (key: string) => string }) {
    if (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) {
        return null;
    }
    return (
        <Box sx={{
            position: 'absolute',
            left: '8px',
            top: '0px',
            backgroundColor: '#e53935',
            color: '#fff',
            borderRadius: '4px',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 700,
            fontFamily: 'var(--font-title)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
        }}>
            {t('live')}
        </Box>
    );
}

export function PrizePlanTable({ round, theme, t }: RoundBoxSettledContentProps) {
    const turnoverCurrency = round.operatorTurnover ? round.operatorTurnover.currency : 'NA';

    const smallText: SxProps<Theme> = {
        fontSize: "14px",
        lineHeight: "21px",
        color: 'var(--font-color-supportive)',
        fontWeight: "400",
        fontFamily: 'var(--font-title)'
    };

    const smallTitleWin: SxProps<Theme> = {
        fontSize: "12px",
        color: 'var(--text-black)',
        lineHeight: "20px",
        fontWeight: "700",
        fontFamily: 'var(--font-title)',
    };

    const groups = round.operatorPrizePlan?.payoutGroups || [];
    const maxNum = round.preliminaryCorrectRow?.length || 0;
    const whole = turnoverCurrency === 'SEK' || isCurrencyWithWholeNumbers(turnoverCurrency as string);

    const alternatingRow: SxProps<Theme> = theme === themeC.GoGo ? {} :
        theme === themeC.Hard ? {
            '&:nth-of-type(odd)': { backgroundColor: '#282828' },
            '&:nth-of-type(even)': { backgroundColor: '#383838' },
        } : theme === themeC.Green ? {
            '&:nth-of-type(odd)': { backgroundColor: '#eeeeee' },
            '&:nth-of-type(even)': { backgroundColor: 'var(--background_2)' },
        } : {
            '&:nth-of-type(odd)': { backgroundColor: 'var(--background_1)' },
            '&:nth-of-type(even)': { backgroundColor: 'var(--background_2)' },
        };

    const hardBorder = theme === themeC.Hard ? { borderColor: '#444444' } : {};

    const headCellSx: SxProps<Theme> = {
        ...smallText,
        ...hardBorder,
        padding: '6px 6px',
        letterSpacing: 'normal',
    };

    const bodyCellSx: SxProps<Theme> = {
        ...smallTitleWin,
        ...hardBorder,
        padding: '6px 8px',
    };

    if (groups.length === 0) {
        return null;
    }

    return (
        <Grid container sx={{ marginTop: "8px", marginBottom: "8px", padding: "0 8px", minHeight: "130px", alignContent: "flex-start" }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headCellSx}>{t('correct_rows')}</TableCell>
                        <TableCell sx={headCellSx}>{t('winners')}</TableCell>
                        <TableCell align="right" sx={headCellSx}>{t('prize')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map((group, i) => {
                        const numCorrect = maxNum - i;
                        const payout = group.payoutPerRow?.money?.amount ?? 0;
                        const currency = group.payoutPerRow?.money?.currency ?? '';
                        const rows = group.rows;
                        return (
                            <TableRow key={i} sx={alternatingRow}>
                                <TableCell sx={bodyCellSx}>{numCorrect}</TableCell>
                                <TableCell sx={bodyCellSx}>{rows ? (Number(rows) < 1 ? Math.ceil(Number(rows)) : Math.round(Number(rows))) : 'NA'} {t('st')}</TableCell>
                                <TableCell align="right" sx={bodyCellSx}>{formatStringCurrency(payout, currency, t, whole)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Grid>
    );
}

function RoundBoxSettledContent({
    round,
    theme,
    t,
}: RoundBoxSettledContentProps) {
    const link = `/rounds/${round.id}`;
    const turnoverAmount = round.operatorTurnover ? Number(round.operatorTurnover.amount) : 'NA';
    const turnoverCurrency = round.operatorTurnover ? round.operatorTurnover.currency : 'NA';

    const smallText: SxProps<Theme> = {
        fontSize: "14px",
        lineHeight: "21px",
        color: 'var(--font-color-supportive)',
        fontWeight: "400",
        fontFamily: 'var(--font-title)'
    };

    const smallTitle: SxProps<Theme> = {
        fontSize: "16px",
        color: 'var(--text-black)',
        lineHeight: "20px",
        fontWeight: "700",
        fontFamily: 'var(--font-title)',
    };

    const smallTitleUpper: SxProps<Theme> = {
        fontSize: "16px",
        color: 'var(--text-black)',
        lineHeight: "20px",
        fontWeight: "700",
        fontFamily: 'var(--font-title)',
        textTransform: 'uppercase'
    };

    const dateString = getDateStringForClosed(round, t);

    const isLive = round.status === 'ACTIVE';

    return (
        <>
            <Grid container direction="column" justifyContent="space-evenly"
                sx={{ marginTop: "5px", marginBottom: "11px", position: "relative" }}>
                {isLive && <LiveBadge theme={theme} t={t} />}
                <Grid item>
                    <Grid container alignItems="center" direction="column">
                        <Typography sx={smallText}>{t('turnover')}</Typography>
                        <Typography sx={smallTitle}>{formatStringCurrency(turnoverAmount, turnoverCurrency, t, true)}</Typography>
                    </Grid>
                </Grid>
                <Grid item sx={{ marginTop: "10px" }}>
                    <Grid container alignItems="center" direction="column">
                        <Typography sx={smallText}>{t('started')}</Typography>
                        <Typography sx={smallTitleUpper}>{dateString}</Typography>
                    </Grid>
                </Grid>
            </Grid>

            <PrizePlanTable round={round} theme={theme} t={t} />

            <Grid container direction="row" justifyContent="space-evenly" sx={{ marginTop: "5px", marginBottom: "25px" }}>
                <Grid item>
                    <Link to={link}>
                        <div>
                            {theme !== themeC.Dbet && !IsLeo(theme) &&
                                <Button>{t('show_round')}</Button>
                            }
                            {theme === themeC.Dbet &&
                                <Button padding="8px 35px">
                                    <Grid container direction="row" alignItems="center">
                                        <Typography sx={{ marginRight: "10px", fontWeight: "700" }}>{t('show_round')}</Typography>
                                        <Grid>
                                            <Grid container direction="column" justifyContent="center" height="100%">
                                                {DbetArrowRight}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Button>
                            }
                            {(IsLeo(theme)) &&
                                <Button>{t('show_round')}</Button>
                            }
                        </div>
                    </Link>
                </Grid>
            </Grid>
        </>
    );
}

export default RoundBoxSettledContent;
